import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Device } from "@twilio/voice-sdk";
import { useAuth, API } from "@/App";
import { toast } from "sonner";

/**
 * Global in-app calling (Twilio Programmable Voice — outbound only).
 * Manages a single Twilio Device + the active Call, exposed via context so any
 * "Call" button across the app can place a call and a single floating widget
 * renders the in-call controls.
 */
const VoiceCallContext = createContext(null);
export const useVoiceCall = () => useContext(VoiceCallContext);

const cleanNumber = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  if (s.startsWith("+")) return s;
  const d = s.replace(/\D/g, "");
  if (d.length === 10) return `+1${d}`;
  if (d.length === 11 && d.startsWith("1")) return `+${d}`;
  return d ? `+${d}` : "";
};

const _ua = typeof navigator !== "undefined" ? (navigator.userAgent || "") : "";
const _plat = typeof navigator !== "undefined" ? (navigator.platform || "") : "";
const isMac = /Mac|iPad|iPhone|iPod/.test(_plat || _ua);
const isWindows = /Win/.test(_plat) || /Windows/.test(_ua);
const MAC_HINT = isMac
  ? " On a Mac, also open System Settings → Privacy & Security → Microphone and make sure your browser is turned on."
  : "";
const WIN_HINT = isWindows
  ? " On Windows, also open Settings → Privacy & security → Microphone and turn on \"Microphone access\" and \"Let desktop apps access your microphone\"."
  : "";
const OS_HINT = MAC_HINT || WIN_HINT;

/**
 * Requests microphone access and returns { ok } or { ok:false, message }.
 * Triggers the browser permission prompt (must be called from a user gesture).
 * Maps failures to actionable, platform-aware guidance so users know exactly
 * how to grant access when no prompt appears (already-denied / OS-blocked).
 */
async function requestMicrophone() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, message: "This browser can't access the microphone. Please use the latest Chrome, Edge or Safari over a secure (https) connection." };
  }
  // If permission was previously denied, the browser will NOT show a prompt —
  // tell the user how to re-enable it instead of failing silently.
  try {
    const status = await navigator.permissions?.query?.({ name: "microphone" });
    if (status && status.state === "denied") {
      return {
        ok: false,
        message: `Microphone access is blocked for this site, so no permission prompt will appear. Click the microphone (or lock) icon in your browser's address bar, set Microphone to "Allow", then reload the page and try again.${OS_HINT}`,
      };
    }
  } catch { /* permissions API not available — fall through to getUserMedia */ }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Release the probe stream immediately; Twilio re-acquires the mic on connect.
    stream.getTracks().forEach((t) => t.stop());
    return { ok: true };
  } catch (err) {
    const name = err?.name || "";
    if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
      return {
        ok: false,
        message: `Microphone access was denied. Click the microphone (or lock) icon in your browser's address bar, set Microphone to "Allow", then reload the page and try again.${OS_HINT}`,
      };
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
      return { ok: false, message: "No microphone was found. Please connect or enable a microphone, then try again." };
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
      return { ok: false, message: "Your microphone is being used by another app (e.g. Zoom, FaceTime, Teams). Close it and try again." };
    }
    return { ok: false, message: `Couldn't access the microphone. Click the microphone (or lock) icon in your browser's address bar, set Microphone to "Allow", then reload the page and try again.${OS_HINT}` };
  }
}

export function VoiceCallProvider({ children }) {
  const { token } = useAuth();
  const [enabled, setEnabled] = useState(null); // null=unknown; gate for UI
  const [planGated, setPlanGated] = useState(false); // creds exist but plan blocks
  const [callState, setCallState] = useState("idle"); // idle|connecting|ringing|in-call|ended|error
  const [callInfo, setCallInfo] = useState(null); // {to, contactName, projectId, audience, direction}
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [errorMsg, setErrorMsg] = useState(null);
  const [incoming, setIncoming] = useState(null); // {from} when a call is ringing in

  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const incomingCallRef = useRef(null);
  const startedAtRef = useRef(null);
  const autoHideRef = useRef(null);

  // Probe whether calling is available for this tenant/deployment.
  useEffect(() => {
    let cancelled = false;
    const id = setTimeout(() => {
      if (!token) { if (!cancelled) setEnabled(false); return; }
      axios.get(`${API}/voice/config`, { headers: { Authorization: `Bearer ${token}` } })
        .then((res) => {
          if (cancelled) return;
          setEnabled(!!res.data?.enabled);
          setPlanGated(!!res.data?.plan_gated);
        })
        .catch(() => { if (!cancelled) setEnabled(false); });
    }, 0);
    return () => { cancelled = true; clearTimeout(id); };
  }, [token]);

  // In-call timer
  useEffect(() => {
    if (callState !== "in-call") return;
    const iv = setInterval(() => {
      if (startedAtRef.current) {
        setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(iv);
  }, [callState]);

  const teardownCall = useCallback(() => {
    callRef.current = null;
    startedAtRef.current = null;
  }, []);

  const ensureDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;
    const res = await axios.post(`${API}/voice/token`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const accessToken = res.data?.token;
    const device = new Device(accessToken, { logLevel: "error", codecPreferences: ["opus", "pcmu"] });
    device.on("error", (err) => {
      setErrorMsg(err?.message || "Device error");
    });
    // Incoming call → show the ringing widget and let the user accept/reject.
    device.on("incoming", (call) => {
      // Ignore a second incoming call while already busy.
      if (callRef.current || incomingCallRef.current) {
        try { call.reject(); } catch { /* ignore */ }
        return;
      }
      incomingCallRef.current = call;
      const from = call?.parameters?.From || "";
      setIncoming({ from });
      call.on("cancel", () => { incomingCallRef.current = null; setIncoming(null); });
      call.on("disconnect", () => { setCallState("ended"); teardownCall(); });
      call.on("error", () => { incomingCallRef.current = null; setIncoming(null); });
    });
    await device.register();
    deviceRef.current = device;
    return device;
  }, [token, teardownCall]);

  // Proactively register the device when calling is enabled so the browser can
  // RECEIVE inbound calls. We deliberately do NOT request the microphone here —
  // requesting it without a user gesture (on page load) can get auto-dismissed
  // and leave the site permission stuck in "denied". Mic is requested when the
  // user actually clicks Call / Accept.
  useEffect(() => {
    if (enabled !== true || !token) return;
    let cancelled = false;
    (async () => {
      try {
        if (!cancelled) await ensureDevice();
      } catch { /* device will be created lazily on first call */ }
    })();
    return () => { cancelled = true; };
  }, [enabled, token, ensureDevice]);

  const acceptIncoming = useCallback(async () => {
    const call = incomingCallRef.current;
    if (!call) return;
    const mic = await requestMicrophone();
    if (!mic.ok) { setErrorMsg(mic.message); toast.error(mic.message); return; }
    callRef.current = call;
    incomingCallRef.current = null;
    setIncoming(null);
    setErrorMsg(null);
    setMuted(false);
    setElapsed(0);
    setCallInfo({ to: call?.parameters?.From || "", contactName: "", direction: "inbound" });
    call.on("accept", () => { startedAtRef.current = Date.now(); setCallState("in-call"); });
    call.on("disconnect", () => { setCallState("ended"); teardownCall(); });
    call.on("error", (err) => { setErrorMsg(err?.message || "Call failed"); setCallState("error"); teardownCall(); });
    try { call.accept(); setCallState("in-call"); startedAtRef.current = Date.now(); }
    catch { setCallState("error"); }
  }, [teardownCall]);

  const rejectIncoming = useCallback(() => {
    const call = incomingCallRef.current;
    if (call) { try { call.reject(); } catch { /* ignore */ } }
    incomingCallRef.current = null;
    setIncoming(null);
  }, []);

  const startCall = useCallback(async ({ to, contactName = "", projectId = null, audience = "client" }) => {
    if (enabled === false) {
      toast.error(planGated
        ? "In-app calling is an Enterprise feature. Ask your admin to enable it."
        : "In-app calling isn't set up yet.");
      return;
    }
    if (callState === "connecting" || callState === "ringing" || callState === "in-call") {
      toast.error("You're already on a call.");
      return;
    }
    const number = cleanNumber(to);
    if (!number) { toast.error("No valid phone number to call."); return; }

    setErrorMsg(null);
    setMuted(false);
    setElapsed(0);
    setCallInfo({ to: number, contactName, projectId, audience });
    setCallState("connecting");

    try {
      // Surface mic-permission issues up front (required for WebRTC audio).
      const mic = await requestMicrophone();
      if (!mic.ok) {
        setCallState("error");
        setErrorMsg(mic.message);
        return;
      }
    } catch {
      setCallState("error");
      setErrorMsg("Microphone access is required to make calls. Please allow mic access and try again.");
      return;
    }

    try {
      const device = await ensureDevice();
      const call = await device.connect({
        params: { To: number, ContactName: contactName, ProjectId: projectId || "", Audience: audience },
      });
      callRef.current = call;

      call.on("ringing", () => setCallState("ringing"));
      call.on("accept", () => { startedAtRef.current = Date.now(); setCallState("in-call"); });
      call.on("disconnect", () => { setCallState("ended"); teardownCall(); });
      call.on("cancel", () => { setCallState("ended"); teardownCall(); });
      call.on("reject", () => { setCallState("ended"); teardownCall(); });
      call.on("error", (err) => {
        setErrorMsg(err?.message || "Call failed");
        setCallState("error");
        teardownCall();
      });
    } catch (e) {
      setErrorMsg(e?.response?.data?.detail || e?.message || "Could not start the call.");
      setCallState("error");
      teardownCall();
    }
  }, [enabled, planGated, callState, ensureDevice, teardownCall]);

  const hangup = useCallback(() => {
    if (callRef.current) {
      try { callRef.current.disconnect(); } catch { /* ignore */ }
    } else {
      setCallState("ended");
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (!callRef.current) return;
    const next = !muted;
    try { callRef.current.mute(next); setMuted(next); } catch { /* ignore */ }
  }, [muted]);

  const closeWidget = useCallback(() => {
    setCallState("idle");
    setCallInfo(null);
    setErrorMsg(null);
    setElapsed(0);
  }, []);

  // Auto-hide the widget shortly after a call ends.
  useEffect(() => {
    if (callState === "ended" || callState === "error") {
      autoHideRef.current = setTimeout(() => closeWidget(), 5000);
      return () => clearTimeout(autoHideRef.current);
    }
  }, [callState, closeWidget]);

  // Destroy the device on unmount / logout.
  useEffect(() => {
    return () => {
      if (deviceRef.current) {
        try { deviceRef.current.destroy(); } catch { /* ignore */ }
        deviceRef.current = null;
      }
    };
  }, []);

  const value = {
    enabled, planGated, callState, callInfo, muted, elapsed, errorMsg,
    startCall, hangup, toggleMute, closeWidget,
    incoming, acceptIncoming, rejectIncoming,
  };

  return <VoiceCallContext.Provider value={value}>{children}</VoiceCallContext.Provider>;
}
